import { useEffect, useState, useMemo } from "react";
import moment from "moment";
import { formatNumber } from "utils/utils";
import { CreditStatus } from "./CreditStatus";
import { useTranslation } from "react-i18next";
import { FontFamily, FontSize } from "config/font";
import { LabelStyled } from "components/form/inputs/inputs.styled";
import {
  TipCell,
  StatusCell,
  TipHeadCell,
  WeekContainer,
  TipColumnDivider,
  CreditGridHeader,
  CreditCellContainer,
} from "../tips.styled";
import { CircularProgress } from "@material-ui/core";
import { Text } from "components/Typography/text.styled";
import { CreditDeposit, CreditWithdrawal } from "model/TipCredit";
import { companyCredits } from "services/api/company/companyCredits.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";

type UnifiedCreditItem = {
  id: number;
  type: "deposit" | "withdrawal";
  amount: number;
  status: string;
  reference: string;
  createdAt: string;
};

export const CreditHistory = () => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const companyId = companyState.company?.id;
  const [isLoading, setIsLoading] = useState(true);
  const [sortDescending, setSortDescending] = useState(true);
  const [creditItems, setCreditItems] = useState<UnifiedCreditItem[]>([]);
  const [groupedByWeek, setGroupedByWeek] = useState<
    Record<number, UnifiedCreditItem[]>
  >({});

  const sortedItems = useMemo(() => {
    return [...creditItems].sort((a, b) =>
      sortDescending
        ? moment(b.createdAt).diff(moment(a.createdAt))
        : moment(a.createdAt).diff(moment(b.createdAt))
    );
  }, [creditItems, sortDescending]);

  useEffect(() => {
    const fetchData = async () => {
      if (!companyId) return;

      try {
        const [depositsRes, withdrawalsRes] = await Promise.all([
          companyCredits.getCreditDeposits(companyId),
          companyCredits.getCreditWithdrawals(companyId),
        ]);

        const deposits: UnifiedCreditItem[] = depositsRes.data.deposits.map(
          (d: CreditDeposit) => ({
            id: d.id,
            type: "deposit",
            amount: d.amount,
            status: d.status,
            reference: d.reference,
            createdAt: d.createdAt,
          })
        );

        const withdrawals: UnifiedCreditItem[] =
          withdrawalsRes.data.withdrawals.map((w: CreditWithdrawal) => ({
            id: w.id,
            type: "withdrawal",
            amount: w.amount,
            status: w.status,
            reference: w.reference,
            createdAt: w.createdAt,
          }));

        setCreditItems([...deposits, ...withdrawals]);
      } catch (err) {
        console.error("Could not fetch deposits/withdrawals", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [companyId]);

  useEffect(() => {
    const grouped: Record<number, UnifiedCreditItem[]> = {};

    sortedItems.forEach((item) => {
      const week = moment(item.createdAt).isoWeek();
      if (!grouped[week]) grouped[week] = [];
      grouped[week].push(item);
    });

    setGroupedByWeek(grouped);
  }, [sortedItems]);

  if (isLoading) return <CircularProgress />;

  if (creditItems.length === 0) {
    return <Text>{t("TipCredit.NoDepositsOrWithdrawalsFound")}</Text>;
  }

  return (
    <>
      <LabelStyled>{t("TipCredit.CreditHelpText")}</LabelStyled>
      <CreditGridHeader style={{ marginTop: "20px" }}>
        <TipHeadCell
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            gap: "6px",
          }}
          onClick={() => setSortDescending((prev) => !prev)}
        >
          <Text
            fontSize={FontSize.Large}
            fontFamily={FontFamily.MontserratSemiBold}
            style={{ userSelect: "none" }}
          >
            {t("TipCredit.Date")}
          </Text>
          <span
            style={{
              userSelect: "none",
              outline: "none",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            {sortDescending ? "↓" : "↑"}
          </span>
        </TipHeadCell>

        <TipHeadCell>
          <Text
            fontSize={FontSize.Large}
            fontFamily={FontFamily.MontserratSemiBold}
          >
            {t("TipCredit.Amount")}
          </Text>
        </TipHeadCell>

        <TipHeadCell>
          <Text
            fontSize={FontSize.Large}
            fontFamily={FontFamily.MontserratSemiBold}
          >
            {t("TipCredit.Type")}
          </Text>
        </TipHeadCell>

        <TipHeadCell>
          <Text
            fontSize={FontSize.Large}
            fontFamily={FontFamily.MontserratSemiBold}
          >
            {t("TipCredit.Status")}
          </Text>
        </TipHeadCell>

        <TipHeadCell>
          <Text
            fontSize={FontSize.Large}
            fontFamily={FontFamily.MontserratSemiBold}
          >
            {t("TipCredit.Reference")}
          </Text>
        </TipHeadCell>
      </CreditGridHeader>
      {Object.entries(groupedByWeek)
        .sort(([weekA], [weekB]) =>
          sortDescending
            ? Number(weekB) - Number(weekA)
            : Number(weekA) - Number(weekB)
        )

        .map(([week, items]) => (
          <div key={week}>
            <WeekContainer>
              <Text fontFamily={FontFamily.MontserratSemiBold}>
                {t("TipCredit.Week")} {week}
              </Text>
            </WeekContainer>

            {items.map((item) => (
              <CreditCellContainer
                key={`${item.type}-${item.id}`}
                isSelected={false}
                isExpanded={false}
              >
                <TipCell>
                  <Text>
                    {moment(item.createdAt).format("ddd DD/MM").toUpperCase()}
                  </Text>
                </TipCell>

                <TipColumnDivider />

                <TipCell>
                  <Text>
                    {item.type === "withdrawal" ? "-" : "+"}
                    &nbsp;{formatNumber(item.amount)} {t("CompanyTip.Kr")}
                  </Text>
                </TipCell>

                <TipColumnDivider />

                <TipCell>
                  <Text>
                    {item.type === "withdrawal"
                      ? t("TipCredit.Withdrawal")
                      : t("TipCredit.Deposit")}
                  </Text>
                </TipCell>

                <TipColumnDivider />

                <StatusCell>
                  <CreditStatus status={item.status} />
                </StatusCell>

                <TipColumnDivider />

                <TipCell>
                  <Text>{item.reference}</Text>
                </TipCell>
              </CreditCellContainer>
            ))}
          </div>
        ))}
    </>
  );
};
